import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useSSEStream } from '../../../hooks/useSSEStream';
import type { AgentStatusEntry, ProspectSearchStatusEvent } from '../types';

interface UseProspectSearchStreamOptions {
  sessionRef: string | undefined;
  organizationId: string | undefined;
  getAccessToken?: () => Promise<string | null>;
}

interface UseProspectSearchStreamReturn {
  agents: AgentStatusEntry[];
  searchStatus: string;
  totalResults: number | undefined;
  isConnected: boolean;
}

export function useProspectSearchStream({
  sessionRef,
  organizationId,
  getAccessToken,
}: UseProspectSearchStreamOptions): UseProspectSearchStreamReturn {
  const [agents, setAgents] = useState<AgentStatusEntry[]>([]);
  const [searchStatus, setSearchStatus] = useState<string>('idle');
  const [totalResults, setTotalResults] = useState<number | undefined>();
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const sessionRefRef = useRef(sessionRef);
  sessionRefRef.current = sessionRef;
  const savedResultsRef = useRef<{ sessionRef: string; count: number } | undefined>(undefined);

  const handleMessage = useCallback((data: string) => {
    try {
      const event = JSON.parse(data) as ProspectSearchStatusEvent;

      if (sessionRefRef.current && event.sessionRef !== sessionRefRef.current) {
        return;
      }

      setAgents(event.agents);
      setSearchStatus(event.status);

      if (event.totalResults !== undefined) {
        setTotalResults(event.totalResults);
      }

      const previous = savedResultsRef.current;
      const moreResultsSaved =
        event.status === 'searching' &&
        event.totalResults !== undefined &&
        (previous?.sessionRef !== event.sessionRef || previous.count !== event.totalResults);
      if (event.totalResults !== undefined) {
        savedResultsRef.current = { sessionRef: event.sessionRef, count: event.totalResults };
      }

      if (moreResultsSaved || event.status === 'completed' || event.status === 'failed') {
        queryClientRef.current.invalidateQueries({
          queryKey: ['prospect-search'],
        });
      }
    } catch {
      // Ignore malformed messages
    }
  }, []);

  const { isConnected } = useSSEStream({
    path: 'prospect-search/status/stream',
    organizationId,
    enabled: !!organizationId,
    getAccessToken,
    onMessage: handleMessage,
  });

  return { agents, searchStatus, totalResults, isConnected };
}
